// New Relic telemetry for the Manager View vehicle-switch flow (US2018171).
//
// All emission for the vehicle-switch experience lives here (and is invoked from
// changeVehicleMiddleware) so the shared activities-change-vehicle activity stays free of any
// New Relic wiring. Every event carries `appName: ApplicationName.CMD` so the events can be
// attributed to Manager View. The mandatory deal-correlation params (dealXgId, dealXgVersion,
// mmdDealerId, correlationId) are set once as global New Relic custom attributes at deal init
// (postBootstrapMiddleware) and ride along on every page action automatically.
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { ApplicationName } from '@makemydeal/dr-shared-types';

export type VehicleSwitchOutcome = 'success' | 'upqFallback' | 'cashFallback' | 'noRetry';

export type VehicleSwitchFailureContext = {
    errorCategory?: string;
    isEcommerceDeal: boolean;
    isAddNewScenario: boolean;
};

export type RetentionCategory = 'incentives' | 'vpp' | 'accessories' | 'taxes';

export type VehicleSwitchVehicleInfo = {
    year: number;
    make: string;
    model: string;
    trim: string;
    stockNumber?: string;
    condition?: string;
};

export type VehicleSwitchVehicles = {
    previousVehicle: VehicleSwitchVehicleInfo;
    newVehicle: VehicleSwitchVehicleInfo;
};

// Captured at CHANGE_VEHICLE_CLICKED (before the switch mutates state) and attached to the
// dispatched SWITCH_VEHICLE action's meta so the terminal PAYMENT_RECEIVED / PAYMENT_FAILURE
// handlers can read the original vehicles and keepAll decision back without any middleware state.
export type VehicleSwitchInfo = {
    vehicles: VehicleSwitchVehicles;
    keepAll: boolean;
    totalItemsAvailable: number;
    totalItemsRetained: number;
};

export const VEHICLE_SWITCH_EVENTS = {
    completed: 'MV:VehicleSwitch:Completed',
    categoryRetention: 'MV:VehicleSwitch:CategoryRetention',
    upqFallbackTriggered: 'MV:VehicleSwitch:UPQFallbackTriggered',
    upqNoRetry: 'MV:VehicleSwitch:UPQNoRetry',
    cashFallbackTriggered: 'MV:VehicleSwitch:CashFallbackTriggered'
} as const;

const baseAttributes = () => ({ appName: ApplicationName.CMD });

const normalizeVehicle = (vehicle: any): VehicleSwitchVehicleInfo => {
    const source = vehicle || {};
    return {
        year: source.year || 0,
        make: source.make || '',
        model: source.model || '',
        trim: source.trim || '',
        stockNumber: source.stockNumber,
        condition: source.condition
    };
};

const formatVehicle = (vehicle: VehicleSwitchVehicleInfo): string =>
    `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`.trim();

export const buildVehicleSwitchVehicles = (previousRaw: any, newRaw: any): VehicleSwitchVehicles => ({
    previousVehicle: normalizeVehicle(previousRaw),
    newVehicle: normalizeVehicle(newRaw)
});

const failureAttributes = ({ errorCategory, isEcommerceDeal, isAddNewScenario }: VehicleSwitchFailureContext) => ({
    errorCategory: errorCategory ?? '',
    isEcommerceDeal,
    isAddNewScenario
});

export const logVehicleSwitchCompleted = (
    info: VehicleSwitchInfo,
    outcome: VehicleSwitchOutcome,
    failureContext: VehicleSwitchFailureContext,
    upqFallbackTriggered: boolean = outcome !== 'success'
): void => {
    const { vehicles, keepAll, totalItemsAvailable, totalItemsRetained } = info;
    logNewRelicPageAction(VEHICLE_SWITCH_EVENTS.completed, {
        ...baseAttributes(),
        ...failureAttributes(failureContext),
        outcome,
        upqFallbackTriggered,
        keepAll,
        totalItemsAvailable,
        totalItemsRetained,
        previousVehicle: formatVehicle(vehicles.previousVehicle),
        previousCondition: vehicles.previousVehicle.condition ?? '',
        newVehicle: formatVehicle(vehicles.newVehicle),
        newCondition: vehicles.newVehicle.condition ?? ''
    });
};

export const logCategoryRetention = (category: RetentionCategory, keptCount: number, totalCount: number): void => {
    logNewRelicPageAction(VEHICLE_SWITCH_EVENTS.categoryRetention, {
        ...baseAttributes(),
        category,
        keptCount,
        removedCount: totalCount - keptCount,
        totalCount
    });
};

export const logUpqFallbackTriggered = (): void => {
    logNewRelicPageAction(VEHICLE_SWITCH_EVENTS.upqFallbackTriggered, { ...baseAttributes() });
};

export const logUpqNoRetry = (failureContext: VehicleSwitchFailureContext): void => {
    logNewRelicPageAction(VEHICLE_SWITCH_EVENTS.upqNoRetry, { ...baseAttributes(), ...failureAttributes(failureContext) });
};

export const logCashFallbackTriggered = (failureContext: VehicleSwitchFailureContext): void => {
    logNewRelicPageAction(VEHICLE_SWITCH_EVENTS.cashFallbackTriggered, {
        ...baseAttributes(),
        ...failureAttributes(failureContext)
    });
};
